'use client';

import { c as _c } from "react/compiler-runtime";
import { CardHeader } from '@achra/ui/card';
import { cn } from '@achra/ui/lib/utils';
import { Landmark } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { Maybe } from '@/modules/__generated__/graphql/switchboard-generated';
import { BILLING_CYCLE_SUFFIXES } from '@/modules/service-purchase/lib/constants';
import { createStickyObserver, STICKY_HEADER_SM_BREAKPOINT } from '@/modules/service-purchase/lib/sticky-observer-utils';
import { formatPrice, formatSummaryPrice } from '@/modules/service-purchase/lib/utils';
import { usePurchaseTotals, useSelectedBillingCycle, useSelectedTier } from '@/modules/service-purchase/providers/service-purchase-store-provider';
interface SummaryCardHeaderProps {
  templateTitle?: string;
  templateSubtitle?: Maybe<string>;
}
function SummaryCardHeader(t0) {
  const $ = _c(36);
  const {
    templateTitle,
    templateSubtitle
  } = t0;
  const totals = usePurchaseTotals();
  const selectedTier = useSelectedTier();
  const selectedBillingCycle = useSelectedBillingCycle();
  const sentinelRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const sentinel = sentinelRef.current;
      if (!sentinel) {
        return;
      }
      let observer = createStickyObserver(sentinel, setIsStuck);
      const mql = window.matchMedia(STICKY_HEADER_SM_BREAKPOINT);
      const handleBreakpointChange = () => {
        observer.disconnect();
        observer = createStickyObserver(sentinel, setIsStuck);
      };
      mql.addEventListener("change", handleBreakpointChange);
      return () => {
        mql.removeEventListener("change", handleBreakpointChange);
        observer.disconnect();
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div ref={sentinelRef} className="-mb-6 h-0 w-full" aria-hidden="true" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = isStuck && "border-primary/30 shadow-primary/20 items-center rounded-xl border py-2 transition-all duration-500 lg:py-3";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("sticky top-18 z-20 flex flex-row items-start justify-between gap-4 px-3 pt-3 sm:top-27.5 lg:px-6 lg:pt-6", "bg-card rounded-t-xl transition-shadow", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="bg-primary flex size-12 shrink-0 items-center justify-center rounded-2xl"><Landmark className="text-primary-foreground size-6" strokeWidth={1.5} /></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== templateTitle) {
    t7 = <h2 className="text-foreground text-sm/5.5 leading-tight font-bold lg:text-lg lg:leading-[120%]">{templateTitle}</h2>;
    $[6] = templateTitle;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== templateSubtitle) {
    t8 = templateSubtitle && <span className="text-foreground/70 text-xs font-medium lg:text-sm/5.5">{templateSubtitle}</span>;
    $[8] = templateSubtitle;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7 || $[11] !== t8) {
    t9 = <div className="flex items-start gap-2">{t6}<div className="flex flex-col justify-center gap-1 self-stretch">{t7}{t8}</div></div>;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== selectedTier.isCustomPricing || $[14] !== selectedTier.name) {
    t10 = !(selectedTier.name === "Custom" && selectedTier.isCustomPricing) && <span className="text-foreground text-sm/5.5 font-bold lg:text-base/6">{selectedTier.name}</span>;
    $[13] = selectedTier.isCustomPricing;
    $[14] = selectedTier.name;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = BILLING_CYCLE_SUFFIXES[selectedBillingCycle];
  let t12;
  if ($[16] !== selectedTier.isCustomPricing || $[17] !== selectedTier.pricing.currency || $[18] !== t11 || $[19] !== totals.recurringTotal) {
    t12 = formatSummaryPrice({
      amount: totals.recurringTotal,
      isRecurring: true,
      suffix: t11,
      isCustomPricing: selectedTier.isCustomPricing,
      currency: selectedTier.pricing.currency
    });
    $[16] = selectedTier.isCustomPricing;
    $[17] = selectedTier.pricing.currency;
    $[18] = t11;
    $[19] = totals.recurringTotal;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12) {
    t13 = <span className="text-primary text-base/6 font-semibold lg:leading-7">{t12}</span>;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t10 || $[24] !== t13) {
    t14 = <div className="flex flex-col items-end sm:flex-row sm:items-baseline sm:gap-2">{t10}{t13}</div>;
    $[23] = t10;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== selectedTier.pricing.currency || $[27] !== totals.setupTotal) {
    t15 = totals.setupTotal > 0 && <span className="text-foreground/70 text-xs/4.5 font-medium">+ {formatPrice(totals.setupTotal, selectedTier.pricing.currency)} Setup</span>;
    $[26] = selectedTier.pricing.currency;
    $[27] = totals.setupTotal;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14 || $[30] !== t15) {
    t16 = <div className="flex shrink-0 flex-col items-end gap-1">{t14}{t15}</div>;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t16 || $[33] !== t5 || $[34] !== t9) {
    t17 = <>{t3}<CardHeader className={t5}>{t9}{t16}</CardHeader></>;
    $[32] = t16;
    $[33] = t5;
    $[34] = t9;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  return t17;
}
export { SummaryCardHeader };